import {
  Box,
  Button,
  ButtonProps,
  Card,
  CardContent,
  Divider,
  Stack,
  styled,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React from "react";
import variables from "../../../../assets/styles/variables.module.scss";
import DoneIcon from "@mui/icons-material/Done";


interface PricingTier {
  amount: number;
  currency: string;
  duration: string;
}

interface MyComponentProps {
  amount: number|string
  duration?: string
  title: string
  summary: string[]
  body: string[]
  action: string
  color: string
  onActionClick?: () => void
  disabled?: boolean
  pricingTiers?: PricingTier[]
  selectedDuration?: string
  onDurationChange?: (duration: string) => void
  showDurationSelector?: boolean
}

interface ColorButtonProps extends ButtonProps {
  customColor: string; // This will allow a custom color to be passed in
}

const ColorButton = styled(Button)<ColorButtonProps>(({ theme, customColor }) => ({
  color: variables.white,
  backgroundColor: customColor,
  "&:hover": {
    backgroundColor: customColor,
  },
}));

const LargeCard: React.FC<MyComponentProps> = (props) => {
  return (
    <Card
      elevation={6}
      sx={{
        borderRadius: "12px",
        height: "100%",
        display: "flex", // Add flexbox to align content
        flexDirection: "column", // Ensure children are stacked vertically
      }}
    >
      <CardContent
        sx={{
          padding: {
            md: "24px", // Desktop
            flexGrow: 1, // Allow CardContent to grow and take available space
            display: "flex", // Use flexbox
            flexDirection: "column", // Stack children vertically
          },
        }}
      >
        <Box display="flex" alignItems="baseline">
          <Typography
            component="span"
            sx={{
              fontWeight: "700", // Matches the bold style
              color: "#0D1C2E", // Adjust the color to your needs
              fontSize: "32px"
            }}
          >
            {/* props */}

            {typeof props.amount=='string'?props.amount:`$${props.amount}`}
          </Typography>
          <Typography
            variant="subtitle2" // Adjusts the size of "/month"
            component="span"
            sx={{
              marginLeft: "0.25em", // Adds a bit of spacing
              color: "#0D1C2E", // Ensure consistent color
            }}
          >
            {/* props */}

            {props.duration && `/${props.duration}`}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "30px",
            fontWeight: "500",
            color: props.color,
            mt: 2,
            mb: 2,
          }}
        >
          {props.title}
        </Typography>
        {/* props */}
        {props.body.map((item) => (
          <Typography>{item} </Typography>
        ))}

        <Divider sx={{ my: 3 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >

          {/* props */}
          {props.summary.map((item) => (
            <>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                <DoneIcon sx={{ color: props.color, fontSize:"18px" }}/>
                <Typography variant="body2">{item}</Typography>
              </Stack>
            </>
          ))}

        </Box>
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Duration Selector */}
        {props.showDurationSelector && props.pricingTiers && props.pricingTiers.length > 1 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={props.selectedDuration || props.pricingTiers[0]?.duration}
              exclusive
              onChange={(_, value) => value && props.onDurationChange?.(value)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                  border: `1px solid ${props.color}`,
                  color: props.color,
                  '&.Mui-selected': {
                    backgroundColor: props.color,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: props.color,
                    }
                  }
                }
              }}
            >
              {props.pricingTiers.map((tier) => (
                <ToggleButton key={tier.duration} value={tier.duration}>
                  {tier.duration.charAt(0).toUpperCase() + tier.duration.slice(1)}: ${tier.amount}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
        
        <Box display="flex" justifyContent="center" mt="30px" mb="10px">
          <ColorButton 
            disableElevation 
            variant="contained" 
            customColor={props.color}
            disabled={props.disabled}
            onClick={props.onActionClick}
            sx={{
              textTransform:"capitalize"
            }}
          >
            {props.action}
          </ColorButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LargeCard;
